import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Timer, Trophy, MessageSquare, User,
  LogOut, Settings, Shield, Users, BookOpen
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import { AVATARS, getSpineClass } from '../lib/utils';
import StreakBadge from '../components/StreakBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import PageFlipper from './PageFlipper';
import { usePageFlip } from './usePageFlip';
import { paperTextureSVG } from './pageTexture';
import { groupsApi } from '../lib/apiClient';

const NAV_LINKS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', key: 'dashboard' },
  { to: '/study', icon: Timer, label: 'Study', key: 'study' },
  { to: '/leaderboard', icon: Trophy, label: 'Leaderboard', key: 'leaderboard' },
  { to: '/chat', icon: MessageSquare, label: 'Chat', key: 'chat' },
  { to: '/profile', icon: User, label: 'Profile', key: 'profile' },
];

export default function BookShell({ children }) {
  const { user, role, signOut, groups, setGroup } = useAuthStore();
  const isSystemAdmin = role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'admin';
  const { groupName, fetchGroupData } = useGroupStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // Core Page Flipping Hook
  const {
    isFlipping,
    direction,
    flipProgress,
    phase,
    displayLocation,
    handleTouchStart,
    handleTouchEnd
  } = usePageFlip();

  // Content preservation for flipping (front and back page views)
  const [oldContent, setOldContent] = useState(null);
  const [currentContent, setCurrentContent] = useState(children);

  useEffect(() => {
    if (children !== currentContent) {
      setOldContent(currentContent);
      setCurrentContent(children);
    }
  }, [children, currentContent]);

  useEffect(() => {
    if (user?.groupId) {
      fetchGroupData(user.groupId);
    }
  }, [user?.groupId, fetchGroupData]);

  const streak = user?.streak ?? user?.streakDays ?? 0;
  const streakLevel = streak >= 30 ? 3 : streak >= 7 ? 2 : streak > 0 ? 1 : 0;
  const avatar = user?.avatar !== undefined ? AVATARS[user.avatar % AVATARS.length] : AVATARS[0];

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };

  const currentRouteName = location.pathname.split('/')[1] || 'login';

  // Format Roman Numerals for Page Numbers
  const getRomanPageNum = (pathname, side) => {
    const route = pathname.split('/')[1] || 'login';
    const baseNum = {
      'login': 1,
      'onboarding': 3,
      'dashboard': 5,
      'study': 7,
      'leaderboard': 9,
      'chat': 11,
      'profile': 13,
      'leader': 15,
      'admin': 17
    }[route] || 5;

    return side === 'left' ? toRoman(baseNum) : toRoman(baseNum + 1);
  };

  const toRoman = (num) => {
    const lookup = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 };
    let roman = '';
    for (let i in lookup) {
      while (num >= lookup[i]) {
        roman += i;
        num -= lookup[i];
      }
    }
    return roman;
  };

  // Skip book frame layout for login and onboarding screens to focus on the center cards
  const isLoginOrOnboarding = location.pathname === '/login' || location.pathname === '/' || location.pathname === '/onboarding';

  if (isLoginOrOnboarding) {
    return (
      <div 
        className="w-full min-h-screen relative flex items-center justify-center bg-[#090e1a] overflow-y-auto py-8"
        style={{ '--paper-texture-url': `url("${paperTextureSVG}")` }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className="book-outer-container select-none"
      style={{
        '--paper-texture-url': `url("${paperTextureSVG}")`,
        pointerEvents: isFlipping ? 'none' : 'auto'
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={(e) => handleTouchEnd(e, navigate)}
    >
      <div className="book-body">
        
        {/* ── Left Page (Static Navigation / Sidebar) ── */}
        <aside 
          className="book-page-left flex flex-col"
          data-page-num={getRomanPageNum(location.pathname, 'left')}
        >
          {/* Logo */}
          <div className="p-5 border-b border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', boxShadow: '0 0 16px rgba(99,102,241,0.4)' }}>
              <BookOpen size={18} className="text-white animate-pulse" />
            </div>
            <div>
              <div className="heading font-bold text-white text-sm tracking-wide">StudyLeague</div>
              <div className="text-white/30 text-xs font-medium">Dark Academics</div>
            </div>
          </div>

          {/* User Profile Info */}
          {user && (
            <div className="p-4 mx-3 my-2 rounded-xl bg-white/5 border border-white/10 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg shadow-inner"
                  style={{ background: avatar.bg, boxShadow: `0 0 0 2px #090e1a, 0 0 0 4px ${avatar.bg}50` }}
                >
                  {avatar.emoji}
                </div>
                <div className="overflow-hidden flex-1">
                  <div className="text-white font-semibold text-sm truncate">{user.displayName || user.name}</div>
                  <div className="text-white/40 text-xs capitalize">
                    {role === 'vice_leader' || role === 'vice-leader' ? 'Vice-Leader' : role || 'Member'}
                  </div>
                </div>
              </div>
              {!isSystemAdmin && (
                <div className="flex items-center justify-between border-t border-white/5 pt-2 mt-1">
                  <StreakBadge streak={streak} />
                </div>
              )}
            </div>
          )}

          {/* Nav Links */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {!isSystemAdmin && NAV_LINKS.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                    isActive ? 'text-white' : 'text-white/50 hover:text-white/80'
                  }`
                }
                style={({ isActive }) => isActive ? {
                  background: 'rgba(99,102,241,0.15)',
                  border: '1px solid rgba(99,102,241,0.25)',
                  boxShadow: '0 0 12px rgba(99,102,241,0.2)',
                } : {
                  background: 'transparent',
                  border: '1px solid transparent',
                }}
              >
                <Icon size={18} className="flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm font-medium truncate">{label}</span>
              </NavLink>
            ))}

            {/* Leader Panel link */}
            {(role === 'leader' || role === 'vice_leader' || role === 'vice-leader') && (
              <NavLink
                to="/leader"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive ? 'text-amber-400 font-semibold' : 'text-amber-400/50 hover:text-amber-400/80'
                  }`
                }
                style={({ isActive }) => isActive ? {
                  background: 'rgba(245,158,11,0.15)',
                  border: '1px solid rgba(245,158,11,0.25)',
                } : { background: 'transparent', border: '1px solid transparent' }}
              >
                <Settings size={18} className="flex-shrink-0 group-hover:rotate-45 transition-transform duration-200" />
                <span className="text-sm font-medium truncate">Leader Panel</span>
              </NavLink>
            )}

            {/* Admin Panel link */}
            {isSystemAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive ? 'text-red-400 font-semibold' : 'text-red-400/50 hover:text-red-400/80'
                  }`
                }
                style={({ isActive }) => isActive ? {
                  background: 'rgba(239,68,68,0.15)',
                  border: '1px solid rgba(239,68,68,0.25)',
                } : { background: 'transparent', border: '1px solid transparent' }}
              >
                <Shield size={18} className="flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm font-medium truncate">Admin Panel</span>
              </NavLink>
            )}
          </nav>

          {/* Group details and Switcher */}
          {(groups?.length > 0) && (
            <div className="p-4 border-t border-white/5 space-y-2">
              <div className="text-white/40 text-[10px] uppercase font-bold tracking-widest mb-2 px-1">My Groups</div>
              {groups.map(g => (
                <button
                  key={g.id}
                  onClick={() => setGroup(g.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all duration-200 ${
                    user?.groupId === g.id
                      ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                      : 'text-white/60 hover:bg-white/5 hover:text-white/90 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Users size={14} className="flex-shrink-0" />
                    <span className="text-xs font-semibold truncate">{g.name}</span>
                  </div>
                  {user?.groupId === g.id && (
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                  )}
                </button>
              ))}

              {groups.length < 3 && (
                <button
                  onClick={() => navigate('/onboarding')}
                  className="w-full mt-2 text-left px-3 py-2 rounded-lg text-emerald-400/70 hover:text-emerald-400 hover:bg-emerald-400/10 text-xs transition-all duration-200 flex items-center gap-2"
                >
                  <span>+ Join New Group</span>
                </button>
              )}

              {user?.groupId && (
                <button
                  onClick={() => setShowLeaveConfirm(true)}
                  className="w-full mt-4 text-left px-3 py-2 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-400/10 text-xs transition-all duration-200 flex items-center gap-2"
                >
                  <LogOut size={12} />
                  <span>Leave Active Group</span>
                </button>
              )}
            </div>
          )}

          {/* Sign Out */}
          <div className="p-3 border-t border-white/5">
            <button
              onClick={() => setShowSignOutConfirm(true)}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/5 transition-all duration-200 text-sm"
            >
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </aside>

        {/* ── Glowing Book Spine ── */}
        <div 
          className="book-spine-element"
          data-streak-level={streakLevel}
        />

        {/* ── Right Page (Underlay Content + Flip Overlay) ── */}
        <main 
          className="book-page-right"
          data-page-num={getRomanPageNum(location.pathname, 'right')}
        >
          {/* Static revealed page content */}
          <div className={`page-right-content ${isFlipping ? 'content-hidden' : 'content-visible'}`}>
            {children}
          </div>

          {/* Turning Page Overlay */}
          <PageFlipper
            isFlipping={isFlipping}
            direction={direction}
            flipProgress={flipProgress}
            phase={phase}
            oldContent={oldContent}
            newContent={currentContent}
          />

          {/* 3D cast shadow that tracks page curls */}
          <div 
            className="page-cast-shadow" 
            style={{ '--flip-progress': flipProgress }}
          />

          {/* Corner Bookmarks for aesthetics */}
          <div 
            className="corner-bookmark" 
            data-route={currentRouteName} 
          />
        </main>
      </div>

      {/* ── Mobile Tab Navigation Bar ── */}
      <nav className="mobile-tab-bar">
        {!isSystemAdmin ? (
          NAV_LINKS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-1 py-1 transition-all duration-200 ${
                  isActive ? 'text-indigo-400 scale-105' : 'text-white/40'
                }`
              }
            >
              <Icon size={18} />
              <span className="text-[10px]">{label}</span>
            </NavLink>
          ))
        ) : (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-1 transition-all duration-200 ${
                isActive ? 'text-red-400 scale-105' : 'text-white/40'
              }`
            }
          >
            <Shield size={18} />
            <span className="text-[10px]">Admin Panel</span>
          </NavLink>
        )}
      </nav>

      {/* Confirmation Modals */}
      <ConfirmDialog
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={async () => {
          setShowLeaveConfirm(false);
          try {
            await groupsApi.leave();
            const { groups } = useAuthStore.getState();
            // Need to reload user to get updated groups list
            await useAuthStore.getState().fetchUser();
            
            const updatedGroups = useAuthStore.getState().groups;
            if (updatedGroups && updatedGroups.length > 0) {
              setGroup(updatedGroups[0].id);
              navigate('/dashboard');
            } else {
              setGroup(null);
              navigate('/onboarding');
            }
          } catch (e) {
            console.error('Failed to leave group:', e);
          }
        }}
        title="Leave Active Group?"
        message="Are you sure you want to leave this study league? Your individual stats remain intact, but you will lose group rank standing in this group."
        confirmLabel="Leave Group"
        variant="danger"
      />
      <ConfirmDialog
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={handleSignOut}
        title="Sign Out of League?"
        message="Your sessions are synced safely. You can log back in at any time to resume your studying streaks!"
        confirmLabel="Sign Out"
        variant="warning"
      />
    </div>
  );
}
